import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api";
import { toast } from "sonner";
import { Target, Plus, Pause, Play, Archive, Edit3, X } from "lucide-react";

const STATUS_STYLES = {
  proposed: "bg-slate-800/60 text-slate-300 border-slate-700",
  active:   "bg-emerald-950/60 text-emerald-300 border-emerald-800/60",
  paused:   "bg-amber-950/60 text-amber-300 border-amber-800/60",
  retired:  "bg-slate-800/40 text-slate-500 border-slate-800",
};

function TargetCreator({ onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: "", hypothesis: "", opportunity_signals: "", capability_areas: "",
    exclusions: "", industry_keywords: "", research_budget_usd: "", research_run_cap: "", notes: "",
  });
  const create = useMutation({
    mutationFn: async (body) => (await api.post("/targets", body)).data,
    onSuccess: () => { toast.success("Target created"); qc.invalidateQueries({ queryKey: ["targets"] }); onClose(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Create failed"),
  });
  const toList = (s) => s.split(",").map(x => x.trim()).filter(Boolean);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="target-create-modal">
      <div className="max-w-2xl w-full border border-emerald-800/60 bg-slate-950 rounded-lg p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-medium text-slate-100 flex items-center gap-2"><Target size={16} className="text-emerald-400" /> New Active Target</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"><X size={16} /></button>
        </div>
        <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Target name" data-testid="target-name-input" className="w-full bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-100" />
        <textarea value={form.hypothesis} onChange={(e) => setForm({...form, hypothesis: e.target.value})} placeholder="Business hypothesis — why this target?" data-testid="target-hypothesis-input" className="w-full bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-100 h-20" />
        <input value={form.industry_keywords} onChange={(e) => setForm({...form, industry_keywords: e.target.value})} placeholder="Industry keywords (comma-separated)" data-testid="target-industry-input" className="w-full bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-100" />
        <input value={form.opportunity_signals} onChange={(e) => setForm({...form, opportunity_signals: e.target.value})} placeholder="Opportunity signals (comma-separated)" className="w-full bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-100" />
        <input value={form.capability_areas} onChange={(e) => setForm({...form, capability_areas: e.target.value})} placeholder="Approved KNKT capability areas (comma-separated)" className="w-full bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-100" />
        <input value={form.exclusions} onChange={(e) => setForm({...form, exclusions: e.target.value})} placeholder="Exclusions (comma-separated)" className="w-full bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-100" />
        <div className="grid grid-cols-2 gap-2">
          <input value={form.research_budget_usd} onChange={(e) => setForm({...form, research_budget_usd: e.target.value})} placeholder="Research budget USD" className="bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-100" />
          <input value={form.research_run_cap} onChange={(e) => setForm({...form, research_run_cap: e.target.value})} placeholder="Max research runs" className="bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-100" />
        </div>
        <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Notes" className="w-full bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-100 h-16" />
        <button
          onClick={() => create.mutate({
            ...form,
            opportunity_signals: toList(form.opportunity_signals),
            capability_areas: toList(form.capability_areas),
            exclusions: toList(form.exclusions),
            industry_keywords: toList(form.industry_keywords),
            research_budget_usd: form.research_budget_usd ? Number(form.research_budget_usd) : null,
            research_run_cap: form.research_run_cap ? Number(form.research_run_cap) : null,
          })}
          disabled={!form.name || !form.hypothesis || create.isPending}
          data-testid="target-create-submit"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border bg-emerald-950/60 text-emerald-300 border-emerald-800/60 hover:brightness-125 disabled:opacity-40"
        >
          <Plus size={12} /> Create (Proposed)
        </button>
      </div>
    </div>
  );
}

export default function Targets() {
  const [creating, setCreating] = useState(false);
  const qc = useQueryClient();
  const { data: targets = [], isLoading } = useQuery({
    queryKey: ["targets"],
    queryFn: async () => (await api.get("/targets")).data,
  });
  const patch = useMutation({
    mutationFn: async ({ id, body }) => (await api.patch(`/targets/${id}`, body)).data,
    onSuccess: () => { toast.success("Target updated"); qc.invalidateQueries({ queryKey: ["targets"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Update failed"),
  });
  return (
    <div className="space-y-5" data-testid="targets-view">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-100 flex items-center gap-2"><Target size={22} className="text-emerald-400" /> Active Targets</h1>
          <p className="text-sm text-slate-400 mt-1">Persistent hunting strategies. Qualified Prospects must bind to an Active Target.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="new-target-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border bg-emerald-950/60 text-emerald-300 border-emerald-800/60 hover:brightness-125">
          <Plus size={12} /> New Target
        </button>
      </div>
      {creating && <TargetCreator onClose={() => setCreating(false)} />}
      <div className="border border-slate-800 bg-slate-900/60 rounded-lg overflow-hidden">
        <table className="w-full text-left" data-testid="targets-table">
          <thead>
            <tr className="border-b border-slate-800 font-mono text-[10px] uppercase tracking-widest text-slate-500">
              <th className="px-3 py-2">Target</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Industry keywords</th>
              <th className="px-3 py-2">Budget</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={5} className="px-3 py-4 text-sm text-slate-500">Loading…</td></tr>}
            {!isLoading && targets.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-sm text-slate-500 text-center">No Active Targets yet. Create one to start binding Qualified Prospects.</td></tr>
            )}
            {targets.map((t) => (
              <tr key={t.id} data-testid={`target-row-${t.id}`} className="text-xs border-b border-slate-800/50 font-mono text-slate-300">
                <td className="px-3 py-3 align-top max-w-[320px]">
                  <div className="font-sans text-sm text-slate-200">{t.name}</div>
                  <div className="text-[11px] text-slate-500 line-clamp-2">{t.hypothesis}</div>
                </td>
                <td className="px-3 py-3 align-top">
                  <span className={`inline-flex px-2 py-0.5 rounded text-[11px] border ${STATUS_STYLES[t.status] || STATUS_STYLES.proposed}`} data-testid={`target-status-${t.id}`}>
                    {t.status}
                  </span>
                </td>
                <td className="px-3 py-3 align-top text-[11px] text-slate-400">{(t.industry_keywords || []).join(", ") || "—"}</td>
                <td className="px-3 py-3 align-top text-[11px] text-slate-400">
                  {t.research_budget_usd ? `$${t.research_budget_usd}` : "—"} · {t.cumulative_cost_usd ? `spent $${t.cumulative_cost_usd.toFixed(2)}` : "spent $0"}
                </td>
                <td className="px-3 py-3 align-top space-x-1">
                  {t.status === "proposed" && <button onClick={() => patch.mutate({ id: t.id, body: { status: "active" }})} data-testid={`target-activate-${t.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] border bg-emerald-950/60 text-emerald-300 border-emerald-800/60"><Play size={10}/> Activate</button>}
                  {t.status === "active" && <button onClick={() => patch.mutate({ id: t.id, body: { status: "paused" }})} data-testid={`target-pause-${t.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] border bg-amber-950/60 text-amber-300 border-amber-800/60"><Pause size={10}/> Pause</button>}
                  {t.status === "paused" && <button onClick={() => patch.mutate({ id: t.id, body: { status: "active" }})} data-testid={`target-resume-${t.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] border bg-emerald-950/60 text-emerald-300 border-emerald-800/60"><Play size={10}/> Resume</button>}
                  {t.status !== "retired" && <button onClick={() => patch.mutate({ id: t.id, body: { status: "retired" }})} data-testid={`target-retire-${t.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] border bg-slate-800/60 text-slate-400 border-slate-700"><Archive size={10}/> Retire</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
